import { useRef, useState } from 'react';

import { cn, formatTimeShortRelativeToToday } from 'lib/helpers/utility';
import useLoading from 'lib/hooks/useLoading';

import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Textarea from 'components/shared/Textarea/Textarea';
import Typography from 'components/shared/Typography/Typography';

import Replies from './Replies';

const Comment = ({
  comment = {},
  category,
  refId,
  onDelete,
  onSave,
  creatorId,
  isReply = false
}) => {
  const textareaRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  const [currentComment, setCurrentComment] = useState();
  const [isRepliesExpanded, setIsRepliesExpanded] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);

  const finalComment = currentComment || comment;

  const { loading, startLoading, stopLoading } = useLoading();

  const { creator, created_at, updated_at, value } = finalComment;
  const fullName = creator ? `${creator.f_name} ${creator.l_name}` : 'Unknown';
  const hasEmptyValue = !value?.trim();
  const isDisabled = !currentComment || hasEmptyValue;

  const onEditHandler = () => {
    setIsEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, 100);
  };

  const onToggleDeleteHandler = () => setDeleteWarning((prev) => !prev);

  const commentActions = [
    { icon: 'edit-2-mono', label: 'Edit', onClick: onEditHandler },
    { icon: 'trash-mono', label: 'Delete', onClick: onToggleDeleteHandler }
  ];

  const onChangeHandler = (e) => {
    setCurrentComment({ ...finalComment, value: e.target.value });
  };

  const onCancelHandler = () => {
    setIsEditing(false);
    setCurrentComment();
  };

  const onSaveHandler = async () => {
    startLoading();
    try {
      await onSave(finalComment);
      onCancelHandler();
    } finally {
      stopLoading();
    }
  };

  const handleReplyClick = () => {
    setShowReplyInput((prev) => !prev);
    setIsRepliesExpanded(!showReplyInput);
  };

  const toggleReplies = () => {
    setIsRepliesExpanded((prev) => !prev);
  };

  return (
    <div
      className={cn(
        'flex h-fit w-full flex-col items-start gap-2',
        isReply ? 'pr-4' : 'isolate rounded-lg border border-solid border-neutral-200 p-3'
      )}>
      <div className="flex w-full flex-row items-center gap-1">
        <Avatar className="h-8 w-8" fullName={fullName} />

        <div className="flex w-full items-center justify-between">
          <Typography variant="p" color="neutral-800" size="small">
            {fullName}
          </Typography>

          <div className="flex items-center gap-1">
            {!isEditing && !isReply && (
              <Button
                size="xs"
                variant="link"
                text="Reply"
                onClick={handleReplyClick}
                className="!px-2 text-xs"
              />
            )}

            <Typography color="neutral-500" size="xs" weight="medium">
              {formatTimeShortRelativeToToday(created_at) +
                (updated_at && created_at !== updated_at ? ` (edited)` : '')}
            </Typography>

            {!isEditing && creatorId === creator?.id && (
              <Popover
                options={commentActions}
                icon="context-menu-vertical"
                position="left"
                isFixed
                isBorder={false}
                buttonTheme="transparent"
                activeClassNames={{ button: '!bg-primary-100' }}
              />
            )}
          </div>
        </div>
      </div>

      {isEditing ? (
        <>
          <Typography size="xs">Edit comment</Typography>

          <Textarea
            allowClear
            forwardedRef={textareaRef}
            className="w-full"
            bodyClassName="w-full border-none"
            value={value}
            onChange={onChangeHandler}
          />
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              size="small"
              style="outlined"
              color="neutral"
              text="Cancel"
              onClick={onCancelHandler}
            />

            <Button
              size="small"
              text="Save"
              className={isDisabled ? '!bg-primary-100' : ''}
              isDisabled={isDisabled}
              isLoading={loading}
              onClick={onSaveHandler}
            />
          </div>
        </>
      ) : (
        <Typography size="small" color="neutral-800">
          {value}
        </Typography>
      )}

      {!isReply && (
        <Replies
          commentId={comment.id}
          repliesCount={comment.replies_count}
          category={category}
          refId={refId}
          creatorId={creatorId}
          isExpanded={isRepliesExpanded}
          showReplyInput={showReplyInput}
          onToggleExpanded={toggleReplies}
          onToggleReplyInput={setShowReplyInput}
        />
      )}

      <Confirm
        handleOpen={deleteWarning}
        handleClose={onToggleDeleteHandler}
        handleContinue={() => {
          onDelete(comment.id);
          onToggleDeleteHandler();
        }}
        title="Delete this comment?"
        message="You cannot do undo this action"
        iconColor="danger"
        variant="danger"
        icon="message-remove-mono"
      />
    </div>
  );
};

export default Comment;
